"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { useAuthStore } from "@/store/auth.store";
import { useSidebar } from "@/components/layout/sidebar-context";
import { NotificationDropdown } from "@/components/layout/notification-dropdown";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { Button } from "@/components/ui/button";
import { CachePurgeModal } from "@/components/layout/cache-purge-modal";
import { mediaUrl } from "@/lib/utils";
import { resolveStorefrontUrl } from "@/lib/storefront-url";
import { LogOut, Menu, ExternalLink, PanelLeftClose, PanelLeftOpen, RefreshCw } from "lucide-react";

interface HeaderProps {
  title: string;
  actions?: React.ReactNode;
}

const STOREFRONT_URL =
  process.env.NEXT_PUBLIC_STOREFRONT_URL ??
  process.env.NEXT_PUBLIC_SITE_URL ??
  "";

function getInitials(name?: string) {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function Header({ title, actions }: HeaderProps) {
  const { user, logout } = useAuthStore();
  const router = useRouter();
  const { toggle, collapsed, toggleCollapsed } = useSidebar();
  const storefrontUrl = resolveStorefrontUrl(STOREFRONT_URL);
  const [cacheModalOpen, setCacheModalOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    router.push("/login");
  };

  return (
    <>
    <CachePurgeModal open={cacheModalOpen} onClose={() => setCacheModalOpen(false)} />
    <header className="relative z-30 h-16 border-b border-slate-200 bg-white/95 flex items-center justify-between px-4 sm:px-6 shrink-0 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:border-slate-800 dark:bg-slate-950/86">
      <div className="flex items-center gap-3">
        {/* Hamburger — mobile only */}
        <button
          onClick={toggle}
          className="lg:hidden inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm text-slate-500 transition-all duration-200 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-300 dark:shadow-black/20 dark:hover:border-sky-500/30 dark:hover:bg-sky-500/10 dark:hover:text-sky-200 cursor-pointer"
          aria-label="Menüyü aç"
        >
          <Menu className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={toggleCollapsed}
          className="hidden lg:inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm text-slate-500 transition-all duration-200 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-300 dark:shadow-black/20 dark:hover:border-sky-500/30 dark:hover:bg-sky-500/10 dark:hover:text-sky-200 cursor-pointer"
          aria-label={collapsed ? "Sidebar'ı genişlet" : "Sidebar'ı daralt"}
          title={collapsed ? "Sidebar'ı genişlet" : "Sidebar'ı daralt"}
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>

        <h1 className="text-base font-semibold tracking-tight text-primary">{title}</h1>
      </div>

      <div className="flex items-center gap-1">
        {actions && (
          <>
            <div className="flex items-center gap-2 mr-2">{actions}</div>
            <div className="h-5 w-px bg-slate-200 mx-1 dark:bg-slate-800" />
          </>
        )}

        <button
          onClick={() => setCacheModalOpen(true)}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 bg-white shadow-sm text-sm text-slate-500 transition-all duration-200 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-300 dark:shadow-black/20 dark:hover:border-emerald-500/30 dark:hover:bg-emerald-500/10 dark:hover:text-emerald-300"
          title="Cache yönetimi"
        >
          <RefreshCw className="h-4 w-4" />
          <span className="hidden md:inline">Cache</span>
        </button>

        {storefrontUrl && (
          <a
            href={storefrontUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 bg-white shadow-sm text-sm text-slate-500 transition-all duration-200 hover:border-sky-200 hover:bg-sky-50 hover:text-sky-800 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-300 dark:shadow-black/20 dark:hover:border-sky-500/30 dark:hover:bg-sky-500/10 dark:hover:text-sky-200"
            title="Siteyi yeni sekmede aç"
          >
            <ExternalLink className="h-4 w-4" />
            <span className="hidden md:inline">Siteyi Aç</span>
          </a>
        )}

        <ThemeToggle />

        {/* Notification Bell */}
        <NotificationDropdown />

        <div className="h-5 w-px bg-slate-200 mx-1 dark:bg-slate-800" />

        {/* User Avatar + Name */}
        <Link
          href="/settings/account"
          className="flex items-center gap-2.5 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors dark:hover:bg-slate-900"
          title="Yönetici hesabını düzenle"
        >
          {user?.avatarUrl ? (
            <Image
              src={mediaUrl(user.avatarUrl)}
              alt={user.fullName}
              width={28}
              height={28}
              className="h-7 w-7 rounded-full object-cover shrink-0 shadow-sm"
            />
          ) : (
            <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 shadow-sm bg-gradient-to-br from-primary-dark to-primary-mid">
              <span className="text-[10px] font-bold text-white leading-none">{getInitials(user?.fullName)}</span>
            </div>
          )}
          <div className="hidden sm:block leading-tight">
            <p className="text-sm font-medium leading-none text-primary">{user?.fullName}</p>
            <p className="text-[10px] text-slate-400 mt-0.5 leading-none">Yönetici</p>
          </div>
        </Link>

        {/* Logout */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          title="Çıkış Yap"
          className="h-9 w-9 rounded-lg border border-slate-200 bg-white shadow-sm text-slate-400 transition-all duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-500 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-400 dark:shadow-black/20 dark:hover:border-red-500/30 dark:hover:bg-red-500/10 dark:hover:text-red-300 ml-1 cursor-pointer"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
    </header>
    </>
  );
}
